import marketsAllSpecials from "@faire/web-api/routes/markets/event_type/all-specials";
import { Button } from "@faire/web/slate/Button";
import { Column } from "@faire/web/slate/Layout";
import { getSpacing, Spacer } from "@faire/web/slate/spacing";
import { FaireMarketTheme } from "@faire/web/slate/Theme/EventsTheme";
import { getEventTheme } from "@faire/web/slate/Theme/eventsThemeColors";
import { EventsThemeShapeCombination } from "@faire/web/slate/Theme/EventsThemeShapeCombination";
import { responsiveMediaQueries } from "@faire/web/slate/Theme/responsiveMediaQueries";
import { ThemeProvider } from "@faire/web/slate/Theme/ThemeProvider";
import { Typography } from "@faire/web/slate/Typography";
import { StrictLocalMsg } from "@faire/web/ui/lib/localization";
import { ResponsiveContentV2 } from "@faire/web/ui/ResponsiveContentV2";
import React from "react";
import styled from "styled-components";

import { MarketStore } from "@faire/retailer/stores/domain/Events/FaireMarkets/MarketStore";

interface IProps {
  onClick: () => void;
}

export const HomeFaireMarketFooter: React.FC<IProps> = ({ onClick }) => {
  return (
    <FullWidthWrapper>
      <ResponsiveContentV2
        tabletAndAbove={
          <StyledMarketPrimaryTertiaryShapes
            width="510px"
            height="321px"
            backShapeColor={getEventTheme("faireMarket").decorative5 ?? ""}
            frontShapeColor={getEventTheme("faireMarket").decorative6 ?? ""}
          />
        }
        mobile={
          <StyledMarketPrimaryTertiaryShapes
            width="238px"
            height="150px"
            backShapeColor={getEventTheme("faireMarket").decorative5 ?? ""}
            frontShapeColor={getEventTheme("faireMarket").decorative6 ?? ""}
          />
        }
      />
      <Wrapper>
        <FooterText
          variant={{
            mobileAndAbove: "displaySSerifRegular",
            tabletAndAbove: "displayMSerifRegular",
          }}
        >
          <StrictLocalMsg defaultMessage="Shop thousands of market specials before they’re gone!" />
        </FooterText>
        <Spacer size="4x" />
        <ThemeProvider theme={FaireMarketTheme}>
          <StyledButton
            linkTo={marketsAllSpecials(MarketStore.get().currentEventType)}
            onClick={onClick}
          >
            <StrictLocalMsg
              defaultMessage="Browse participating brands"
              description={{
                text: "Call to action to browse all participating brands",
              }}
            />
          </StyledButton>
        </ThemeProvider>
      </Wrapper>
    </FullWidthWrapper>
  );
};

const FullWidthWrapper = styled.div`
  position: relative;
  background-color: ${getEventTheme("faireMarket").primaryDark};
  overflow: hidden;
`;

const Wrapper = styled(Column)`
  position: relative;
  padding: ${getSpacing("8x", "7x")};
  margin: 0 auto;
  max-width: 1280px;

  ${responsiveMediaQueries.tablet} {
    padding: ${getSpacing("6x", "7x")};
  }

  ${responsiveMediaQueries.mobile} {
    padding: ${getSpacing("4x", "3x")};
  }
`;

const FooterText = styled(Typography).attrs({
  color: getEventTheme("faireMarket").promoTextInverse,
})`
  &&& {
    margin-right: 464px;

    ${responsiveMediaQueries.tablet} {
      margin-right: 260px;
    }

    ${responsiveMediaQueries.mobile} {
      margin-right: ${getSpacing("7x")};
    }
  }
`;

const StyledButton = styled(Button).attrs({
  variant: "secondary",
})`
  width: fit-content;
  border: 0;
`;

const StyledMarketPrimaryTertiaryShapes = styled(
  EventsThemeShapeCombination.faireMarket.comboC
)`
  position: absolute;
  right: -91px;
  bottom: -38px;

  ${responsiveMediaQueries.mobile} {
    right: -44px;
    bottom: -17px;
  }
`;
